import React, { useState, useEffect, useRef, useCallback } from 'react';
import CustomSelect from '../components/shared/CustomSelect';
import HelpButton from '../components/shared/HelpButton';
import ContractViewer from '../components/ContractViewer';
import { useUser } from '../utils/UserContext';
import { rolesAccess } from '../data/map';
import { cssUrl } from '../utils/cssVersion';


const displayDate = (isoDate) => {
  if (!isoDate) return '';
  const [year, month, day] = isoDate.split('-');
  return `${day}/${month}/${year}`;
};
// --- Constants ---
const STATUS_OPTIONS = ['TFN', 'ABN'];

const DAY_NAMES = ['Mon', 'Tues', 'Wed', 'Thur', 'Fri', 'Sat', 'Sun'];
// Maps JS getDay() (0=Sun) to our day index (0=Mon..6=Sun)
const JS_DAY_TO_INDEX = { 1: 0, 2: 1, 3: 2, 4: 3, 5: 4, 6: 5, 0: 6 };
// Maps our day index to the data row index (indices 4-10 in shift data)
const DAY_DATA_OFFSET = 4;

// ===== HELPER: Get which day-of-week indices (0=Mon..6=Sun) fall within a date range =====
const getDaysInRange = (startStr, endStr) => {
  const days = new Set();
  if (!startStr) return days;
  const start = parseLocalDate(startStr);
  const end = parseLocalDate(endStr || startStr);
  if (!start || !end) return days;
  const cursor = new Date(start);
  while (cursor <= end) {
    days.add(JS_DAY_TO_INDEX[cursor.getDay()]);
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
};

// ===== HELPER: Parse date as local noon =====
const parseLocalDate = (str) => {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d, 12, 0, 0);
};

// ===== HELPER: Get last day of a month =====
const getLastDayOfMonth = (year, month) => new Date(year, month + 1, 0).getDate();

// ===== HELPER: Is a YYYY-MM-DD string in a prior month relative to today? =====
const isPriorMonth = (dateStr) => {
  if (!dateStr) return false;
  const [y, m] = dateStr.split('-').map(Number);
  const now = new Date();
  return y < now.getFullYear() || (y === now.getFullYear() && m - 1 < now.getMonth());
};

// ===== HELPER: Get sites for a given owner from shift data =====
const getSitesForOwner = (ownerName, shiftData) => {
  const sites = new Set();
  for (const row of shiftData) {
    if (row[1] === ownerName && row[2]) sites.add(row[2]);
  }
  return [...sites];
};

// ===== HELPER: Get shift layouts for owner + site =====
const getShiftLayouts = (ownerName, site, shiftData) => {
  const layouts = [];
  for (const row of shiftData) {
    if (row[1] === ownerName && row[2] === site) {
      const days = [];
      for (let i = 0; i < 7; i++) {
        days.push(Number(row[DAY_DATA_OFFSET + i]) || 0);
      }
      layouts.push({
        hours: days,
        shiftHours: Number(row[3]) || 0,
        daystatus: row[11] || '',
      });
    }
  }
  return layouts;
};

// ===== HELPER: Format date as YYYY-MM-DD in local time (avoids UTC shift) =====
const formatLocalDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// ===== HELPER: Calculate hours already used per specific date from existing replacements =====
const calculateUsedHoursPerDate = (startStr, endStr, site, ownerName, existingReplacements) => {
  const perDate = {};
  if (!startStr || !existingReplacements.length) return perDate;

  const start = parseLocalDate(startStr);
  const end = parseLocalDate(endStr || startStr);

  // Replacement row: [id, start, end, site, hrs, rStatus, rByStatus, replaced, replacer, one..seven, owner]
  // Day columns: index 9=Mon(one), 10=Tue(two), 11=Wed(three), 12=Thu(four), 13=Fri(five), 14=Sat(six), 15=Sun(seven)
  const dayColOffset = 9;

  const cursor = new Date(start);
  while (cursor <= end) {
    const dayIndex = JS_DAY_TO_INDEX[cursor.getDay()];
    const dayCol = dayColOffset + dayIndex;
    const dateStr = formatLocalDate(cursor);

    for (const rep of existingReplacements) {
      if (dateStr >= rep[1] && dateStr <= rep[2] && site === rep[3] && ownerName === rep[16] && Number(rep[dayCol]) === 1) {
        perDate[dateStr] = (perDate[dateStr] || 0) + (Number(rep[4]) || 0);
      }
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return perDate;
};

// ===== MAIN COMPONENT =====
const NewReplacement = () => {
  const hasFetched = useRef(false);

  // --- User & role ---
  const { user } = useUser();
  const isAdmin = rolesAccess.admin.includes(user?.userJob || '');

  // --- Data ---
  const [isLoading, setIsLoading] = useState(true);
  const [tfnShiftData, setTfnShiftData] = useState([]);
  const [tfnNames, setTfnNames] = useState([]);
  const [abnShiftData, setAbnShiftData] = useState([]);
  const [abnNames, setAbnNames] = useState([]);
  const [abnSingleNames, setAbnSingleNames] = useState([]);
  const [existingReplacements, setExistingReplacements] = useState([]);

  // --- Form ---
  const [formData, setFormData] = useState({
    startDate: '',
    endDate: '',
    ownerStatus: '',
    ownerName: '',
    replacedStatus: '',
    replacedName: '',
    fillingStatus: '',
    fillingName: '',
    site: '',
    shiftIndex: '',
    hours: '',
  });

  // --- Day selections (which days the replacement covers) ---
  const [selectedDays, setSelectedDays] = useState({
    Mon: false, Tues: false, Wed: false, Thur: false, Fri: false, Sat: false, Sun: false,
  });

  // --- Derived shift data ---
  const [availableSites, setAvailableSites] = useState([]);
  const [shiftLayouts, setShiftLayouts] = useState([]);
  const [activeLayout, setActiveLayout] = useState(null);
  const [usedHoursPerDate, setUsedHoursPerDate] = useState({});

  // --- UI ---
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState(new Set());
  const [toast, setToast] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  // --- Scroll refs ---
  const formFieldsRef = useRef(null);
  const lastSectionRef = useRef(null);
  const prevFieldKeyRef = useRef('');

  // ─── Load stylesheets ───
  useEffect(() => {
    const link1 = document.createElement('link');
    link1.rel = 'stylesheet';
    link1.href = cssUrl('newuseronboarding.css');
    document.head.appendChild(link1);
    const link2 = document.createElement('link');
    link2.rel = 'stylesheet';
    link2.href = cssUrl('filterTemplate.css');
    document.head.appendChild(link2);
    return () => {
      document.head.removeChild(link1);
      document.head.removeChild(link2);
    };
  }, []);

  // ─── Fetch all data ───
  useEffect(() => {
    if (hasFetched.current) return;
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [tfnRes, abnRes, abnNamesRes, existingRes] = await Promise.all([
          fetch('/api/replacement/tfn-shifts'),
          fetch('/api/replacement/abn-shifts'),
          fetch('/api/replacement/abn-names'),
          fetch('/api/replacement/existing'),
        ]);
        const [tfnData, abnData, abnNamesData, existingData] = await Promise.all([
          tfnRes.json(), abnRes.json(), abnNamesRes.json(), existingRes.json(),
        ]);
        setTfnShiftData(tfnData.all || []);
        setTfnNames(tfnData.names || []);
        setAbnShiftData(abnData.all || []);
        setAbnNames(abnData.names || []);
        setAbnSingleNames(abnNamesData.names || []);
        setExistingReplacements(existingData.all || []);
      } catch (err) {
        console.error('Failed to load replacement data:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
    hasFetched.current = true;
  }, []);

  // ─── Toast ───
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // ─── Gentle scroll to next field when a field is populated ───
  useEffect(() => {
    const key = [
      formData.startDate ? 'sd' : '',
      formData.endDate ? 'ed' : '',
      formData.ownerStatus ? 'os' : '',
      formData.ownerName ? 'on' : '',
      formData.replacedStatus ? 'rs' : '',
      formData.replacedName ? 'rn' : '',
      formData.fillingStatus ? 'fs' : '',
      formData.fillingName ? 'fn' : '',
      formData.site ? 'si' : '',
      activeLayout !== null ? 'al' : '',
    ].filter(Boolean).join('-');

    if (key !== prevFieldKeyRef.current && prevFieldKeyRef.current !== '') {
      if (key.length > prevFieldKeyRef.current.length) {
        setTimeout(() => {
          if (formFieldsRef.current) {
            const fields = formFieldsRef.current.querySelectorAll('.ob-field');
            if (fields.length > 0) {
              fields[fields.length - 1].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
          }
        }, 200);
      }
    }
    prevFieldKeyRef.current = key;
  }, [
    formData.startDate, formData.endDate, formData.ownerStatus, formData.ownerName,
    formData.replacedStatus, formData.replacedName, formData.fillingStatus, formData.fillingName,
    formData.site, activeLayout,
  ]);

  // ─────────────────────────────────────────────
  //  VISIBILITY
  // ─────────────────────────────────────────────
  const hasOwner = formData.ownerStatus && formData.ownerName;
  const showReplacedStatus = !!hasOwner;
  const showReplacedName = showReplacedStatus && !!formData.replacedStatus;
  const showFillingStatus = showReplacedName && !!formData.replacedName;
  const showFillingName = showFillingStatus && !!formData.fillingStatus;
  const showSite = showFillingName && !!formData.fillingName;
  const showShifts = showSite && !!formData.site && shiftLayouts.length > 0;
  const showDays = showShifts && activeLayout !== null;
  const showHours = showDays && Object.values(selectedDays).some(Boolean);

  // ─────────────────────────────────────────────
  //  FIELD HANDLERS
  // ─────────────────────────────────────────────
  const setValue = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setTouched(prev => new Set([...prev, field]));
  };

  const clearDates = () => {
    setFormData(prev => ({ ...prev, startDate: '', endDate: '' }));
    resetDays();
    setUsedHoursPerDate({});
    setErrors(prev => { const n = { ...prev }; delete n.startDate; delete n.endDate; return n; });
  };

  const handleOwnerStatusChange = (val) => {
    setFormData({
      startDate: formData.startDate, endDate: formData.endDate,
      ownerStatus: val, ownerName: '', replacedStatus: '', replacedName: '',
      fillingStatus: '', fillingName: '', site: '', shiftIndex: '', hours: '',
    });
    setAvailableSites([]);
    setShiftLayouts([]);
    setActiveLayout(null);
    resetDays();
    setUsedHoursPerDate({});
    setErrors({});
  };

  const handleOwnerNameChange = (val) => {
    setValue('ownerName', val);
    setFormData(prev => ({
      ...prev, ownerName: val, replacedStatus: '', replacedName: '',
      fillingStatus: '', fillingName: '', site: '', shiftIndex: '', hours: '',
    }));
    setShiftLayouts([]);
    setActiveLayout(null);
    resetDays();
    setUsedHoursPerDate({});

    const data = formData.ownerStatus === 'TFN' ? tfnShiftData : abnShiftData;
    setAvailableSites(getSitesForOwner(val, data));
  };

  const handleReplacedStatusChange = (val) => {
    setFormData(prev => ({
      ...prev, replacedStatus: val, replacedName: '',
      fillingStatus: '', fillingName: '', site: '', shiftIndex: '', hours: '',
    }));
    setShiftLayouts([]);
    setActiveLayout(null);
    resetDays();
    setUsedHoursPerDate({});
  };

  const handleReplacedNameChange = (val) => {
    setFormData(prev => ({
      ...prev, replacedName: val,
      fillingStatus: '', fillingName: '', site: '', shiftIndex: '', hours: '',
    }));
    setShiftLayouts([]);
    setActiveLayout(null);
    resetDays();
    setUsedHoursPerDate({});
  };

  const handleFillingStatusChange = (val) => {
    setFormData(prev => ({
      ...prev, fillingStatus: val, fillingName: '', site: '', shiftIndex: '', hours: '',
    }));
    setShiftLayouts([]);
    setActiveLayout(null);
    resetDays();
    setUsedHoursPerDate({});
  };

  const handleFillingNameChange = (val) => {
    setFormData(prev => ({ ...prev, fillingName: val, site: '', shiftIndex: '', hours: '' }));
    setShiftLayouts([]);
    setActiveLayout(null);
    resetDays();
    setUsedHoursPerDate({});
  };

  const handleSiteChange = (val) => {
    setValue('site', val);
    setFormData(prev => ({ ...prev, site: val, shiftIndex: '', hours: '' }));
    resetDays();

    const data = formData.ownerStatus === 'TFN' ? tfnShiftData : abnShiftData;
    const layouts = getShiftLayouts(formData.ownerName, val, data);
    setShiftLayouts(layouts);
    setActiveLayout(layouts.length === 1 ? 0 : null);

    recalcUsedHours(formData.startDate, formData.endDate, val, formData.ownerName);
  };

  const handleShiftSelect = (val) => {
    const idx = parseInt(val, 10);
    setActiveLayout(isNaN(idx) ? null : idx);
    resetDays();
    setValue('shiftIndex', val);
    setValue('hours', '');
  };

  const resetDays = () => {
    setSelectedDays({ Mon: false, Tues: false, Wed: false, Thur: false, Fri: false, Sat: false, Sun: false });
  };

  // Find the actual calendar dates for each selected day within the date range
  const getSelectedDayDates = (days, startStr, endStr) => {
    const result = {};
    if (!startStr) return result;
    const start = parseLocalDate(startStr);
    const end = parseLocalDate(endStr || startStr);
    const cursor = new Date(start);
    while (cursor <= end) {
      const dayIndex = JS_DAY_TO_INDEX[cursor.getDay()];
      const dayName = DAY_NAMES[dayIndex];
      if (days[dayName]) {
        result[dayIndex] = formatLocalDate(cursor);
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    return result;
  };

  const toggleDay = (day) => {
    const dayIndex = DAY_NAMES.indexOf(day);
    const isSelecting = !selectedDays[day];

    if (isSelecting) {
      const daysInRange = getDaysInRange(formData.startDate, formData.endDate);
      if (!daysInRange.has(dayIndex)) {
        setToast({ type: 'error', message: `${day} does not fall within ${formData.startDate} to ${formData.endDate}` });
        return;
      }

      if (activeLayout !== null) {
        const shiftHrs = shiftLayouts[activeLayout].hours[dayIndex];
        if (shiftHrs > 0) {
          const start = parseLocalDate(formData.startDate);
          const end = parseLocalDate(formData.endDate || formData.startDate);
          const cursor = new Date(start);
          const fullDates = [];
          while (cursor <= end) {
            if (JS_DAY_TO_INDEX[cursor.getDay()] === dayIndex) {
              const dateStr = formatLocalDate(cursor);
              const used = usedHoursPerDate[dateStr] || 0;
              if (used >= shiftHrs) {
                fullDates.push(dateStr);
              }
            }
            cursor.setDate(cursor.getDate() + 1);
          }
          if (fullDates.length > 0) {
            setToast({ type: 'error', message: `${fullDates.join(', ')} (${day}) fully allocated — ${shiftHrs} hours used` });
          }
        }
      }
    }

    const newDays = { ...selectedDays, [day]: isSelecting };
    setSelectedDays(newDays);
    setValue('hours', '');

    // ── Auto-narrow dates when fully allocated days exist in range ──
    if (activeLayout !== null && formData.startDate) {
      const layout = shiftLayouts[activeLayout];

      const workingDates = [];
      const start = parseLocalDate(formData.startDate);
      const end = parseLocalDate(formData.endDate || formData.startDate);
      const cursor = new Date(start);

      while (cursor <= end) {
        const di = JS_DAY_TO_INDEX[cursor.getDay()];
        if (layout.hours[di] > 0) {
          const dateStr = formatLocalDate(cursor);
          const used = usedHoursPerDate[dateStr] || 0;
          workingDates.push({
            date: dateStr,
            dayIndex: di,
            dayName: DAY_NAMES[di],
            isFull: used >= layout.hours[di],
          });
        }
        cursor.setDate(cursor.getDate() + 1);
      }

      const hasFullDay = workingDates.some(wd => wd.isFull);

      if (hasFullDay && workingDates.length > 0) {
        let newStartDate = null;
        for (let i = 0; i < workingDates.length; i++) {
          if (!workingDates[i].isFull) {
            newStartDate = workingDates[i].date;
            break;
          }
        }

        let newEndDate = null;
        for (let i = workingDates.length - 1; i >= 0; i--) {
          if (!workingDates[i].isFull) {
            newEndDate = workingDates[i].date;
            break;
          }
        }

        if (newStartDate && newEndDate) {
          if (newStartDate !== formData.startDate || newEndDate !== formData.endDate) {
            setValue('startDate', newStartDate);
            setValue('endDate', newEndDate);
            recalcUsedHours(newStartDate, newEndDate, formData.site, formData.ownerName);
          }
        }
      }
    }
  };

  // Recalculate used hours for current site + owner when dates or site change
  const recalcUsedHours = useCallback((startDate, endDate, site, ownerName) => {
    if (site && ownerName && startDate) {
      const perDate = calculateUsedHoursPerDate(startDate, endDate || startDate, site, ownerName, existingReplacements);
      setUsedHoursPerDate(perDate);
    } else {
      setUsedHoursPerDate({});
    }
  }, [existingReplacements]);

  // Auto-recalculate when dates change and site/owner are already set
  useEffect(() => {
    if (formData.site && formData.ownerName && formData.startDate) {
      const perDate = calculateUsedHoursPerDate(
        formData.startDate, formData.endDate || formData.startDate,
        formData.site, formData.ownerName, existingReplacements
      );
      setUsedHoursPerDate(perDate);
    }
  }, [formData.startDate, formData.endDate, formData.site, formData.ownerName, existingReplacements]);

  // ─────────────────────────────────────────────
  //  DATE HANDLERS — anchor-based (aligned with NewRequestLeave)
  // ─────────────────────────────────────────────
  //
  //  Rules:
  //  • Admin can pick any date (including prior months).
  //  • Non-admin: selecting a prior month → toast + clear BOTH dates.
  //  • START is the anchor. Changing start always resets end to match start;
  //    the user then extends end forward as needed.
  //  • END cannot go before start (toast + reject — user must change start
  //    to shift the range backward).
  //  • END cannot leave start's month (clamped to EOM with a toast).
  //
  const handleStartDateChange = (val) => {
    if (!val) return;

    // Prior-month gate (non-admin only)
    if (!isAdmin && isPriorMonth(val)) {
      setToast({ type: 'error', message: 'Cannot request for a prior month' });
      clearDates();
      return;
    }

    // Start is the anchor — any change to start resets end to match start.
    // The user then extends end forward (within month) as needed.
    const endVal = val;

    setFormData(prev => ({ ...prev, startDate: val, endDate: endVal }));
    setTouched(prev => new Set([...prev, 'startDate', 'endDate']));
    setErrors(prev => { const n = { ...prev }; delete n.startDate; delete n.endDate; return n; });

    // Reset days and recalculate hours-used
    resetDays();
    setValue('hours', '');
    recalcUsedHours(val, endVal, formData.site, formData.ownerName);
  };

  const handleEndDateChange = (val) => {
    if (!val) return;

    // Prior-month gate (non-admin only)
    if (!isAdmin && isPriorMonth(val)) {
      setToast({ type: 'error', message: 'Cannot request for a prior month' });
      clearDates();
      return;
    }

    let startVal = formData.startDate;
    let endVal = val;

    // First-set auto-match: no start yet → start mirrors to end.
    // After this, start becomes the anchor; further end changes follow anchor rules.
    if (!startVal) {
      startVal = val;
      setFormData(prev => ({ ...prev, startDate: startVal, endDate: endVal }));
      setTouched(prev => new Set([...prev, 'startDate', 'endDate']));
      setErrors(prev => { const n = { ...prev }; delete n.startDate; delete n.endDate; return n; });
      resetDays();
      setValue('hours', '');
      recalcUsedHours(startVal, endVal, formData.site, formData.ownerName);
      return;
    }

    // ── Anchor-based rules: start is the anchor, end is constrained ──

    // Floor: end cannot go before start. Change start instead.
    if (endVal < startVal) {
      setToast({
        type: 'error',
        message: 'End date cannot be before start date. Change the start date to shift the range.',
      });
      return;
    }

    // Ceiling: end must stay in the same month as start.
    const [sy, sm] = startVal.split('-').map(Number);
    const [ey, em] = endVal.split('-').map(Number);
    if (sy !== ey || sm !== em) {
      const lastDay = getLastDayOfMonth(sy, sm - 1);
      const pad = (n) => String(n).padStart(2, '0');
      const eomStr = `${sy}-${pad(sm)}-${pad(lastDay)}`;
      setToast({
        type: 'error',
        message: `End date must stay within the same month. Clamped to ${lastDay}th.`,
      });
      endVal = eomStr;
    }

    setFormData(prev => ({ ...prev, startDate: startVal, endDate: endVal }));
    setTouched(prev => new Set([...prev, 'startDate', 'endDate']));
    setErrors(prev => { const n = { ...prev }; delete n.startDate; delete n.endDate; return n; });

    resetDays();
    setValue('hours', '');
    recalcUsedHours(startVal, endVal, formData.site, formData.ownerName);
  };

  const handleHoursChange = (e) => {
    const val = e.target.value.replace(/[^0-9.]/g, '');
    const num = parseFloat(val);

    if (val && !isNaN(num) && num < 0) {
      setToast({ type: 'error', message: 'Hours must be zero or greater' });
      return;
    }

    if (activeLayout !== null && val && !isNaN(num) && num > 0 && formData.startDate) {
      const layout = shiftLayouts[activeLayout];
      const start = parseLocalDate(formData.startDate);
      const end = parseLocalDate(formData.endDate || formData.startDate);
      const cursor = new Date(start);

      while (cursor <= end) {
        const di = JS_DAY_TO_INDEX[cursor.getDay()];
        if (selectedDays[DAY_NAMES[di]] && layout.hours[di] > 0) {
          const dateStr = formatLocalDate(cursor);
          const shiftHoursForDay = layout.hours[di];
          const alreadyUsed = usedHoursPerDate[dateStr] || 0;
          const totalForDay = num + alreadyUsed;
          if (totalForDay > shiftHoursForDay) {
            const remaining = Math.max(0, shiftHoursForDay - alreadyUsed);
            if (remaining <= 0) {
              setToast({ type: 'error', message: `${dateStr} (${DAY_NAMES[di]}) is fully allocated — no hours available` });
            } else {
              setToast({ type: 'error', message: `${dateStr} (${DAY_NAMES[di]}) only has ${remaining.toFixed(1)} hours available` });
            }
            return;
          }
        }
        cursor.setDate(cursor.getDate() + 1);
      }
    }
    setValue('hours', val);
  };

  // ─────────────────────────────────────────────
  //  VALIDATION & SUBMIT
  // ─────────────────────────────────────────────
  const isFormValid = () => {
    if (!formData.startDate || !formData.endDate) return false;
    if (!formData.ownerStatus || !formData.ownerName) return false;
    if (!formData.replacedStatus || !formData.replacedName) return false;
    if (!formData.fillingStatus || !formData.fillingName) return false;
    if (!formData.site) return false;
    if (!Object.values(selectedDays).some(Boolean)) return false;
    if (!formData.hours || parseFloat(formData.hours) <= 0) return false;
    if (Object.keys(errors).length > 0) return false;

    if (activeLayout !== null && formData.startDate) {
      const layout = shiftLayouts[activeLayout];
      const hours = parseFloat(formData.hours) || 0;
      const start = parseLocalDate(formData.startDate);
      const end = parseLocalDate(formData.endDate || formData.startDate);
      const cursor = new Date(start);
      while (cursor <= end) {
        const di = JS_DAY_TO_INDEX[cursor.getDay()];
        if (selectedDays[DAY_NAMES[di]] && layout.hours[di] > 0) {
          const dateStr = formatLocalDate(cursor);
          const used = usedHoursPerDate[dateStr] || 0;
          if (used + hours > layout.hours[di]) return false;
        }
        cursor.setDate(cursor.getDate() + 1);
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!isFormValid() || isSubmitting) return;
    setIsSubmitting(true);

    const payload = {
      ...formData,
      days: selectedDays,
      usedHoursPerDate,
    };

    try {
      const res = await fetch('/processreplacement', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setToast({ type: 'success', message: 'Replacement submitted successfully!' });
        setTimeout(() => window.history.back(), 2000);
      } else {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Submission failed');
      }
    } catch (err) {
      setToast({ type: 'error', message: err.message });
      setIsSubmitting(false);
    }
  };

  const inputClass = (field) =>
    `ob-input ${touched.has(field) && errors[field] ? 'ob-input-error' : ''}`;

  // ─── Employee options based on status ───
  const getNameOptions = (status) => {
    if (status === 'TFN') return tfnNames;
    if (status === 'ABN') return abnSingleNames;
    return [];
  };

  // ─── Shift selector options ───
  const shiftOptions = shiftLayouts.map((layout, i) => {
    const activeDays = DAY_NAMES.filter((_, di) => layout.hours[di] > 0);
    return `${layout.shiftHours}hrs — ${activeDays.join(', ')}`;
  });

  // ─── Loading ───
  if (isLoading) {
    return (
      <div className="ob-page">
        <div className="ob-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
          <div className="fr-loading-state">
            <div className="fr-spinner" />
            <div className="fr-loading-text">Loading Replacement Form…</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ob-page">
      {toast && (
        <div className={`ob-toast ${toast.type === 'success' ? 'ob-toast-success' : 'ob-toast-error'}`}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {toast.type === 'success'
              ? <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              : <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />}
          </svg>
          {toast.message}
        </div>
      )}

      <div className="ob-container">
        <div className="ob-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <h1 className="ob-title">Replacement</h1>
            <p className="ob-subtitle">Fill in the details to submit a shift replacement</p>
          </div>

          <HelpButton helpData={[
            { chip: 'replacement', urlFilter: '/static/pdffilters/ReplacementNewGuide.pdf' }
          ]} />

        </div>

        {pdfUrl && (
          <ContractViewer pdfUrl={pdfUrl} onClose={() => setPdfUrl(null)} />
        )}

        <div className="ob-form-card">
          <div className="ob-form-fields" ref={formFieldsRef} style={{ minHeight: '50vh', maxHeight: '80vh' }}>

            {/* ─── Dates ─── */}
            <div className="ob-field">
              <label className="ob-label">Request Date <span className="ob-label-required">*</span></label>
              <input type="date" value={formData.startDate} onChange={(e) => handleStartDateChange(e.target.value)} className={inputClass('startDate')} />
            </div>

            <div className="ob-field">
              <label className="ob-label">Complete Date <span className="ob-label-required">*</span></label>
              <input type="date" value={formData.endDate} onChange={(e) => handleEndDateChange(e.target.value)} className={inputClass('endDate')} />
            </div>

            <div className="ob-divider" />

            {/* ─── Owner of Shift ─── */}
            <div className="ob-field">
              <CustomSelect label="Owner Status" value={formData.ownerStatus} onChange={handleOwnerStatusChange} options={STATUS_OPTIONS} />
            </div>

            {formData.ownerStatus && (
              <div className="ob-field">
                <CustomSelect
                  label="Owner of Shift"
                  value={formData.ownerName}
                  onChange={handleOwnerNameChange}
                  options={getNameOptions(formData.ownerStatus)}
                />
              </div>
            )}

            {/* ─── Cleaner Being Replaced ─── */}
            {showReplacedStatus && (
              <>
                <div className="ob-divider" />
                <div className="ob-field">
                  <CustomSelect label="Cleaner Being Replaced (Status)" value={formData.replacedStatus} onChange={handleReplacedStatusChange} options={STATUS_OPTIONS} />
                </div>
              </>
            )}

            {showReplacedName && (
              <div className="ob-field">
                <CustomSelect
                  label="Cleaner Being Replaced"
                  value={formData.replacedName}
                  onChange={handleReplacedNameChange}
                  options={getNameOptions(formData.replacedStatus)}
                />
              </div>
            )}

            {/* ─── Cleaner Filling In ─── */}
            {showFillingStatus && (
              <>
                <div className="ob-divider" />
                <div className="ob-field">
                  <CustomSelect label="Cleaner Filling In (Status)" value={formData.fillingStatus} onChange={handleFillingStatusChange} options={STATUS_OPTIONS} />
                </div>
              </>
            )}

            {showFillingName && (
              <div className="ob-field">
                <CustomSelect
                  label="Cleaner Filling In"
                  value={formData.fillingName}
                  onChange={handleFillingNameChange}
                  options={getNameOptions(formData.fillingStatus)}
                />
              </div>
            )}

            {/* ─── Site ─── */}
            {showSite && (
              <>
                <div className="ob-divider" />
                <div className="ob-field">
                  <CustomSelect label="Site Working" value={formData.site} onChange={handleSiteChange} options={availableSites} />
                </div>
              </>
            )}

            {/* ─── Shift Selection ─── */}
            {showShifts && shiftLayouts.length > 1 && (
              <div className="ob-field">
                <CustomSelect
                  label="Select Shift"
                  value={activeLayout !== null ? shiftOptions[activeLayout] : ''}
                  onChange={(val) => handleShiftSelect(String(shiftOptions.indexOf(val)))}
                  options={shiftOptions}
                />
              </div>
            )}

            {/* ─── Shift Info ─── */}
            {showShifts && activeLayout !== null && (
              <>
                <div className="ob-field">
                  <label className="ob-label">Weekday Hours</label>
                  <div className="ob-input ob-input-readonly" style={{ color: '#22d3ee' }}>
                    {Math.max(...shiftLayouts[activeLayout].hours.slice(0, 5))} hrs
                  </div>
                </div>
                {Math.max(...shiftLayouts[activeLayout].hours.slice(5)) > 0 && (
                  <div className="ob-field">
                    <label className="ob-label">Weekend Hours</label>
                    <div className="ob-input ob-input-readonly" style={{ color: '#22d3ee' }}>
                      {Math.max(...shiftLayouts[activeLayout].hours.slice(5))} hrs
                    </div>
                  </div>
                )}
              </>
            )}

            {/* ─── Day Selection ─── */}
            {showDays && (
              <>
                <div className="ob-divider" />
                <div className="ob-field">
                  <label className="ob-label">Select Days <span className="ob-label-required">*</span></label>
                  <div className="ob-helper">Tap the days this replacement covers (only days within your date range are available)</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                    {(() => {
                      const daysInRange = getDaysInRange(formData.startDate, formData.endDate);
                      const dayStatus = {};
                      if (formData.startDate && activeLayout !== null) {
                        const layout = shiftLayouts[activeLayout];
                        const start = parseLocalDate(formData.startDate);
                        const end = parseLocalDate(formData.endDate || formData.startDate);
                        const cursor = new Date(start);
                        while (cursor <= end) {
                          const di = JS_DAY_TO_INDEX[cursor.getDay()];
                          if (layout.hours[di] > 0) {
                            const dateStr = formatLocalDate(cursor);
                            const used = usedHoursPerDate[dateStr] || 0;
                            if (!dayStatus[di]) {
                              dayStatus[di] = { anyFull: false, anyPartial: false, totalDates: 0, fullDates: 0, maxUsed: 0, shiftHrs: layout.hours[di] };
                            }
                            dayStatus[di].totalDates++;
                            dayStatus[di].maxUsed = Math.max(dayStatus[di].maxUsed, used);
                            if (used >= layout.hours[di]) {
                              dayStatus[di].fullDates++;
                              dayStatus[di].anyFull = true;
                            } else if (used > 0) {
                              dayStatus[di].anyPartial = true;
                            }
                          }
                          cursor.setDate(cursor.getDate() + 1);
                        }
                      }

                      return DAY_NAMES.map((day, i) => {
                        const shiftHrs = shiftLayouts[activeLayout].hours[i];
                        if (shiftHrs <= 0) return null;
                        const status = dayStatus[i] || {};
                        const allFull = status.totalDates > 0 && status.fullDates === status.totalDates;
                        const someFull = status.anyFull && !allFull;
                        const somePartial = status.anyPartial && !allFull;
                        const isOutOfRange = !daysInRange.has(i);
                        const isDisabled = allFull || isOutOfRange;
                        return (
                          <button
                            key={day}
                            type="button"
                            onClick={() => toggleDay(day)}
                            style={{
                              padding: '8px 12px',
                              borderRadius: '20px',
                              border: allFull ? '1px solid rgba(239,68,68,0.3)'
                                : isOutOfRange ? '1px solid rgba(255,255,255,0.06)'
                                : selectedDays[day] ? '1px solid #22d3ee'
                                : (someFull || somePartial) ? '1px solid rgba(251,191,36,0.4)'
                                : '1px solid rgba(255,255,255,0.15)',
                              background: allFull ? 'rgba(239,68,68,0.08)'
                                : isOutOfRange ? 'rgba(255,255,255,0.02)'
                                : selectedDays[day] ? 'rgba(34,211,238,0.15)'
                                : 'rgba(255,255,255,0.04)',
                              color: allFull ? '#f87171'
                                : isOutOfRange ? 'rgba(255,255,255,0.2)'
                                : selectedDays[day] ? '#22d3ee'
                                : (someFull || somePartial) ? '#fbbf24'
                                : 'rgba(255,255,255,0.5)',
                              fontWeight: 600,
                              fontSize: '0.8rem',
                              cursor: isDisabled ? 'not-allowed' : 'pointer',
                              transition: 'all 0.2s ease',
                              opacity: isDisabled ? 0.5 : 1,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: '2px',
                            }}
                          >
                            <span>{day}</span>
                            {allFull && <span style={{ fontSize: '0.65rem' }}>Full</span>}
                            {isOutOfRange && !allFull && <span style={{ fontSize: '0.65rem' }}>N/A</span>}
                            {someFull && !isOutOfRange && <span style={{ fontSize: '0.65rem' }}>Some full</span>}
                            {somePartial && !someFull && !isOutOfRange && <span style={{ fontSize: '0.65rem' }}>Partial</span>}
                          </button>
                        );
                      });
                    })()}
                  </div>
                </div>
              </>
            )}

            {/* ─── Replacement Hours ─── */}
            {showHours && (
              <div className="ob-field">
                <label className="ob-label">Replacement Hours <span className="ob-label-required">*</span></label>
                <input
                  type="text" inputMode="decimal" value={formData.hours}
                  onChange={handleHoursChange} placeholder="0"
                  className={inputClass('hours')}
                />
                {(() => {
                  const warnings = [];
                  if (formData.startDate) {
                    const start = parseLocalDate(formData.startDate);
                    const end = parseLocalDate(formData.endDate || formData.startDate);
                    const cursor = new Date(start);
                    while (cursor <= end) {
                      const di = JS_DAY_TO_INDEX[cursor.getDay()];
                      const dateStr = formatLocalDate(cursor);
                      if (selectedDays[DAY_NAMES[di]] && (usedHoursPerDate[dateStr] || 0) > 0) {
                        warnings.push(`${dateStr} (${DAY_NAMES[di]}): ${usedHoursPerDate[dateStr].toFixed(1)}hrs used`);
                      }
                      cursor.setDate(cursor.getDate() + 1);
                    }
                  }
                  if (warnings.length === 0) return null;
                  return (
                    <div className="ob-info-banner ob-info-banner-warning" style={{ marginTop: '8px' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86l-8.6 14.86a1 1 0 00.86 1.51h17.18a1 1 0 00.86-1.51l-8.6-14.86a1 1 0 00-1.72 0z" />
                      </svg>
                      Already allocated: {warnings.join(', ')}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* ─── Summary Card ─── */}
            {formData.ownerName && formData.replacedName && formData.fillingName && formData.site && (
              <>
                <div className="ob-divider" />
                <div className="ob-record-card">
                  <div className="ob-record-header">
                    <div className="ob-record-icon">
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                      </svg>
                    </div>
                    <h3 className="ob-record-title">Replacement Summary</h3>
                  </div>
                  <div className="ob-record-grid">
                    <div className="ob-record-item">
                      <div className="ob-record-item-label">Owner</div>
                      <div className="ob-record-item-value">{formData.ownerName}</div>
                    </div>
                    <div className="ob-record-item">
                      <div className="ob-record-item-label">Replaced</div>
                      <div className="ob-record-item-value">{formData.replacedName}</div>
                    </div>
                    <div className="ob-record-item">
                      <div className="ob-record-item-label">Filling In</div>
                      <div className="ob-record-item-value">{formData.fillingName}</div>
                    </div>
                    <div className="ob-record-item">
                      <div className="ob-record-item-label">Site</div>
                      <div className="ob-record-item-value">{formData.site}</div>
                    </div>
                    <div className="ob-record-item">
                      <div className="ob-record-item-label">Dates</div>
                      <div className="ob-record-item-value">{displayDate(formData.startDate)} → {displayDate(formData.endDate)}</div>
                    </div>
                    {formData.hours && (
                      <div className="ob-record-item">
                        <div className="ob-record-item-label">Hours Per Day</div>
                        <div className="ob-record-item-value" style={{ color: '#22d3ee' }}>{formData.hours}</div>
                      </div>
                    )}
                    {Object.values(selectedDays).some(Boolean) && (
                      <div className="ob-record-item">
                        <div className="ob-record-item-label">Days</div>
                        <div className="ob-record-item-value" style={{ color: '#22d3ee' }}>
                          {DAY_NAMES.filter(d => selectedDays[d]).join(', ')}
                        </div>
                      </div>
                    )}
                    {formData.hours && Object.values(selectedDays).some(Boolean) && (
                      <div className="ob-record-item">
                        <div className="ob-record-item-label">Total Hours</div>
                        <div className="ob-record-item-value" style={{ color: '#4ade80', fontWeight: 700 }}>
                          {(parseFloat(formData.hours) * DAY_NAMES.filter(d => selectedDays[d]).length).toFixed(1)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* ─── Scroll anchor ─── */}
            <div ref={lastSectionRef} />

            {/* ─── Submit ─── */}
            <button
              onClick={handleSubmit}
              disabled={!isFormValid() || isSubmitting}
              className="ob-btn ob-btn-submit"
            >
              {isSubmitting ? (
                <span className="ob-spinner">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Submitting…
                </span>
              ) : (
                'Submit Replacement'
              )}
            </button>

          </div>
        </div>

        <p className="ob-footer">Replacement</p>
      </div>
    </div>
  );
};

export default NewReplacement;